import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { HeaderRow } from "./helpers";

interface Props {
  headers: HeaderRow[];
  disabled: boolean;
  onChange: (headers: HeaderRow[]) => void;
}

export default function ExtraHeaders({ headers, disabled, onChange }: Props) {
  const { t } = useTranslation("storage");
  const classes = useStyles();

  function updateHeader(index: number, update: Partial<HeaderRow>) {
    const updated = headers.map((header, position) => {
      if (position !== index) return header;
      return { ...header, ...update };
    });
    onChange(updated);
  }

  function addHeader() {
    onChange([...headers, { name: "", value: "" }]);
  }

  function removeHeader(index: number) {
    onChange(headers.filter((_, position) => position !== index));
  }

  return (
    <>
      <div className={classes.groupTitle}>
        <Typography variant="body2" className={classes.groupName}>
          {t("externalAI.form.extraHeaders")}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {t("externalAI.form.extraHeadersNote")}
        </Typography>
      </div>
      {headers.map((header, index) => (
        <div className={classes.headerRow} key={index}>
          <TextField
            label={t("externalAI.form.headerName")}
            variant="outlined"
            fullWidth
            disabled={disabled}
            value={header.name}
            placeholder="X-Title"
            InputLabelProps={{ shrink: true }}
            onChange={e => updateHeader(index, { name: e.target.value })}
          />
          <TextField
            label={t("externalAI.form.headerValue")}
            variant="outlined"
            fullWidth
            disabled={disabled}
            value={header.value}
            InputLabelProps={{ shrink: true }}
            onChange={e => updateHeader(index, { value: e.target.value })}
          />
          <IconButton
            disabled={disabled}
            onClick={() => removeHeader(index)}
            aria-label={t("externalAI.form.removeHeader")}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      ))}
      <Button
        color="primary"
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={addHeader}
      >
        {t("externalAI.form.addHeader")}
      </Button>
    </>
  );
}

const useStyles = makeStyles({
  groupTitle: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    margin: "14px 0",
  },
  groupName: { fontWeight: 500 },
  headerRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1.4fr 48px",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
  },
});
